import React from "react"
import { Pressable } from "~/ui/primitives"
import styled from "~/ui/styled"
import { Avatar } from "~widgets/avatar"
import { MessageProps } from "./message.props"
import { presets } from "./palette"
import Status from "./status"
import { hapticTrigger } from "../../utils/haptic"
import { ViewMode } from "~components/chat/view-mode"
import { observer } from "mobx-react-lite"
import { ReplyComponent } from "./message"
import { useChatList } from "~hooks/useChatList"
import { Flex } from "~components/El"

const Container = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 10px;
`

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: calc(100% - 48px);
  min-width: 0;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding-left: 5px;
  padding-top: 5px;
`

const ReceivedMessageName = styled.span`
  color: ${({ theme }) => theme.colors.secondaryText};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: 5px;
  line-height: 1.2;
`

const Main = styled(Flex)<{ isNoBg: boolean }>`
  ${({ isNoBg, theme }) =>
    !isNoBg &&
    `
    background-color: ${theme.colors.blockBackground};
    padding: 10px 12px;
    width: fit-content;
    max-width: 100%;
    word-break: break-word;
    overflow-wrap: break-word;
  `}
  ${({ isNoBg }) =>
    isNoBg &&
    `
    width: fit-content;
    max-width: 100%;
  `}
  border-radius: 10px;
  box-sizing: border-box;
`

const ReplyBarBox = styled.div`
  width: 100%;
  border-left: 2px solid ${({ theme }) => theme.colors.secondary};
  margin-top: ${({ theme }) => theme.space.xs};
  padding-left: ${({ theme }) => theme.space.sm};
  margin-bottom: ${({ theme }) => theme.space.sm};
  box-sizing: border-box;
`

const ReplyBarBoxTitle = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`

const ReplyComponentWrap = styled.div`
  padding-top: ${({ theme }) => theme.space.xs};
  padding-bottom: ${({ theme }) => theme.space.xs};
`

const ReplayBarBoxLeftWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const ForwardMessageText = styled.span`
  color: ${({ theme }) => theme.colors.secondaryText};
  margin-bottom: ${({ theme }) => theme.space.xs};
`

const ForwardMessageName = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`

const AvatarWrap = styled.div`
  flex-shrink: 0;
`

const MessageReceived: React.FC<MessageProps> = observer(
  ({ setPopupOptionListModal, message, Component, isNoBg, chatTypeEnum, ...rest }) => {
    const { openPersonalCard } = useChatList()
    const replyMsg = message.replyMsg
    const user = message.sender
    const replyColor = message.byMe ? presets.blue : presets.green
    const handleSetMode = () => {
      rest?.setMode(ViewMode.ScrollView)
    }
    const handleOnLongPress = () => {
      hapticTrigger()
      if (setPopupOptionListModal) {
        setPopupOptionListModal({ visible: true, message })
      }
    }
    return (
      <Container onClick={handleSetMode}>
        <AvatarWrap>
          <Pressable onClick={() => openPersonalCard({ message, chatTypeEnum })}>
            <Avatar name={user.name} avatar={user.avatar} size={36} />
          </Pressable>
        </AvatarWrap>
        <ContentColumn>
          {["GROUP", "CHANNEL"].includes(chatTypeEnum) && (
            <ReceivedMessageName>{user.name}</ReceivedMessageName>
          )}
          <Main onLongPress={handleOnLongPress} isNoBg={isNoBg}>
            {message.forward_msg_id && (
              <ForwardMessageText>
                转发消息来自: <ForwardMessageName>{message.forward_name}</ForwardMessageName>
              </ForwardMessageText>
            )}
            {message.reply_msg_id && replyMsg && (
              <ReplyBarBox>
                <ReplayBarBoxLeftWrap>
                  <ReplyBarBoxTitle>{replyMsg.sender.name}</ReplyBarBoxTitle>
                  <ReplyComponentWrap>
                    <ReplyComponent message={replyMsg} color={replyColor.text} {...rest} />
                  </ReplyComponentWrap>
                </ReplayBarBoxLeftWrap>
              </ReplyBarBox>
            )}
            {message.reply_msg_id && !replyMsg && (
              <ReplyBarBox>
                <ReplayBarBoxLeftWrap>
                  <ReplyBarBoxTitle>回复讯息不存在</ReplyBarBoxTitle>
                </ReplayBarBoxLeftWrap>
              </ReplyBarBox>
            )}
            <Component
              message={message}
              color={replyColor.text}
              setPopupOptionListModal={setPopupOptionListModal}
              {...rest}
            />
          </Main>
          {!isNoBg && (
            <Row>
              <Status
                message_id={message.message_id}
                time={message.created}
                color={replyColor.smallText}
              />
            </Row>
          )}
        </ContentColumn>
      </Container>
    )
  },
)
export default MessageReceived
